import { Box, Button, Grid, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@material-ui/core';
import AddBox from '@material-ui/icons/AddBox';
import Clear from '@material-ui/icons/Clear';
import DeleteIcon from '@material-ui/icons/Delete';
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { SubmitControl } from '../../components/SubmitControl';
import useSnackbar from '../../hooks/useSnackbar';
import api from '../../services/api/base';
import KeywordCell from './KeywordCell';
import useStyles from './styles';
import { FixedSizeGrid, GridChildComponentProps } from 'react-window';
import { AxiosResponse } from 'axios';
import { IApiResult, ISuccessResult } from '../../services/types';

interface IProps<T> {
  title: string;
  description?: string;
  fetchFactory: () => Promise<AxiosResponse<T[]>>;
  saveFactory: (keyword: string) => Promise<AxiosResponse<IApiResult<T>>>;
  deleteFactory: (id: number) => Promise<AxiosResponse<IApiResult<T>>>;
}

const KeywordManagement = <T extends { id: number; keyword?: string; contents?: string }>(props: IProps<T>): JSX.Element => {
  const classes = useStyles();
  const snackbar = useSnackbar();

  const { title, description, fetchFactory, saveFactory, deleteFactory } = props;

  const [keywords, setKeywords] = useState<T[]>([]);
  const [newKeyword, setNewKeyword] = useState<string>('');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [highlightKeywordId, setHighlightKeywordId] = useState<number>();
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [deletedIds, setDeletedIds] = useState<number[]>([]);

  const filteredKeywords = useMemo<T[]>(() => keywords.filter(keyword => deletedIds.indexOf(keyword.id) === -1), [keywords, deletedIds]);
  const gridRef = useRef<FixedSizeGrid>(null);

  const reset = useCallback(() => {
    setNewKeyword('');
    setCheckedIds([]);
    setDeletedIds([]);
    setIsEditing(false);
  }, []);

  const fetchKeywords = useCallback(async () => {
    try {
      const result = await fetchFactory();

      if (api.isResponseSuccess(result)) {
        setKeywords(result.data);
      } else {
        setKeywords([]);
      }

      reset();
    } catch (e) {
      setKeywords([]);
    }
  }, [fetchFactory, reset]);

  const deleteLateNightKeywords = useCallback(() => {
    setDeletedIds(checkedIds);
  }, [checkedIds]);

  const confirmDeletion = useCallback(async () => {
    if (deletedIds.length > 0) {
      Promise.all(deletedIds.map(id => deleteFactory(id)))
        .then(() => {
          fetchKeywords();
        })
        .catch(e => snackbar.error(`An error has occurred. ${e.message}`));
    } else {
      fetchKeywords();
    }
  }, [deleteFactory, snackbar, fetchKeywords, deletedIds]);

  const addKeyword = useCallback(
    async (keyword: string) => {
      const trimmedKeyword = keyword.trim();

      if (!trimmedKeyword) {
        return;
      }

      try {
        const result = await saveFactory(trimmedKeyword);

        if (api.isResponseSuccess(result)) {
          const { data: added } = result.data as ISuccessResult<T>;
          const { data: addedKeywords } = await fetchFactory();

          setKeywords(addedKeywords);

          reset();

          if (gridRef.current) {
            const index = addedKeywords.findIndex(k => k.id === added.id);
            const rowIndex = Math.ceil(index / 3);

            gridRef.current.scrollToItem({ align: 'smart', rowIndex });
          }

          setHighlightKeywordId(added.id);
        }
      } catch (e) {
        snackbar.error(`An error has occurred. ${e.message}`);
      }
    },
    [saveFactory, snackbar, gridRef, fetchFactory, reset],
  );

  const handleClickClearKeyword = useCallback(() => {
    setNewKeyword('');
  }, []);

  const handleEditClick = useCallback(() => {
    setHighlightKeywordId(undefined);
    setIsEditing(true);
  }, []);

  const handleCancelClick = useCallback(() => reset(), [reset]);

  const handleAddKeywordClick = useCallback(() => addKeyword(newKeyword), [addKeyword, newKeyword]);

  const handleNewKeywordChange = useCallback(e => setNewKeyword(e.target.value), []);

  const handleKeyPress = useCallback(
    e => {
      if (e.key === 'Enter') {
        e.preventDefault();
        addKeyword(newKeyword);
      }
    },
    [addKeyword, newKeyword],
  );

  const handleClickCheckbox = useCallback(
    id => {
      if (checkedIds.indexOf(id) === -1) {
        setCheckedIds(checkedIds.concat([id]));
      } else {
        setCheckedIds(checkedIds.filter(val => val !== id));
      }
    },
    [checkedIds],
  );

  const renderKeywordCell = useCallback(
    (data: GridChildComponentProps) => {
      const { columnIndex, rowIndex, style } = data;
      const idx = rowIndex * 3 + columnIndex;

      const keyword = filteredKeywords[idx];

      if (!keyword) {
        return null;
      }

      return (
        <KeywordCell
          style={style}
          key={keyword.id}
          id={keyword.id}
          keyword={keyword.keyword || keyword.contents || ''}
          isEditing={isEditing}
          highlightKeywordId={highlightKeywordId}
          onClickCheckbox={handleClickCheckbox}
          isChecked={checkedIds.indexOf(keyword.id) !== -1}
        />
      );
    },
    [checkedIds, filteredKeywords, handleClickCheckbox, highlightKeywordId, isEditing],
  );

  useEffect(() => {
    fetchKeywords();
  }, [fetchKeywords]);

  return (
    <Grid container direction="row" justify="flex-start" alignItems="flex-start">
      <Grid item xs={12}>
        <Grid container direction="row" justify="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" component="h2" display="block">
              {title}
            </Typography>
            {description && <Typography className={classes.description}>{description}</Typography>}
          </Box>
          <SubmitControl
            validationMessages={deletedIds.length === 0 ? ['At least one must be deleted.'] : []}
            mode={isEditing ? 'edit' : 'view'}
            onSubmitClick={confirmDeletion}
            onEditClick={handleEditClick}
            onCancelClick={handleCancelClick}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container direction="row" alignItems="center" justify="space-between">
          <Grid item>
            <Grid container direction="row" alignItems="center" justify="flex-start">
              <Tooltip
                title="You can add new keywords in Edit mode. The new keyword will be reflected in the service as soon as it is entered."
                enterDelay={1000}
                arrow
                disableFocusListener={isEditing}
                disableHoverListener={isEditing}
              >
                <TextField
                  disabled={!isEditing}
                  placeholder="Add Keyword"
                  value={newKeyword}
                  onChange={handleNewKeywordChange}
                  onKeyPress={handleKeyPress}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={handleClickClearKeyword}
                          disableFocusRipple
                          style={{ visibility: newKeyword.length > 0 ? 'visible' : 'hidden' }}
                        >
                          <Clear fontSize="small" className={classes.clearButton} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Tooltip>

              <IconButton disabled={!isEditing} onClick={handleAddKeywordClick}>
                <AddBox />
              </IconButton>
            </Grid>
          </Grid>
          <Grid>
            <Button disabled={!isEditing} onClick={deleteLateNightKeywords} className={classes.deleteButton}>
              Delete
              <DeleteIcon />
            </Button>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <div className={classes.lateNightKeywordBox}>
          <FixedSizeGrid
            width={861}
            height={220}
            columnCount={3}
            columnWidth={287}
            rowCount={Math.ceil(filteredKeywords.length / 3)}
            rowHeight={35}
            ref={gridRef}
          >
            {renderKeywordCell}
          </FixedSizeGrid>
        </div>
      </Grid>
    </Grid>
  );
};

export default KeywordManagement;
